/**
 * How many decimals a screen paints (Settings v3 → Global → Decimal Places). A format mask
 * only: values are entered, stored and totalled at their own precision, and changing either
 * number repaints the grid without moving a total. `amount` covers prices, discounts, taxes
 * and totals — percentages and exchange rates included; `quantity` covers line quantities.
 */
export interface DecimalPlaces {
  amount: number;
  quantity: number;
}

/** Choices the settings page offers for each width, in order (amounts 1–6, quantities 1–4) */
export const DECIMAL_PLACES_OPTIONS: Readonly<Record<keyof DecimalPlaces, readonly number[]>> = {
  amount: [1, 2, 3, 4, 5, 6],
  quantity: [1, 2, 3, 4]
};

/**
 * What an organisation that never saved the settings gets — the widths the V3 pipes always
 * painted, so nothing on screen moves for existing tenants.
 */
export const DEFAULT_DECIMAL_PLACES: Readonly<DecimalPlaces> = { amount: 2, quantity: 3 };

/**
 * Width amounts (percentages and exchange rates included) and quantities are entered and stored
 * at. Deliberately not a setting: the widest display choice of each kind, so what the user types
 * is what is kept and no display mask can hide more than a setting could show.
 */
export const STORED_DECIMAL_PLACES: Readonly<DecimalPlaces> = {
  amount: Math.max(...DECIMAL_PLACES_OPTIONS.amount),
  quantity: Math.max(...DECIMAL_PLACES_OPTIONS.quantity)
};

/** Smallest positive value of each kind that survives storage — the floor for quantities and exchange rates */
export const MIN_STORED_VALUE: Readonly<DecimalPlaces> = {
  amount: 1 / 10 ** STORED_DECIMAL_PLACES.amount,
  quantity: 1 / 10 ** STORED_DECIMAL_PLACES.quantity
};

/** A stored value made safe to paint with: one of the choices offered for `kind`, or its default */
export function toDecimalPlaces(kind: keyof DecimalPlaces, value: unknown): number {
  return typeof value === 'number' && DECIMAL_PLACES_OPTIONS[kind].includes(value) ? value : DEFAULT_DECIMAL_PLACES[kind];
}

/** Angular `number` / `formatCurrency` digits info painting exactly `decimals` fraction digits */
export function fixedDigitsInfo(decimals: number): string {
  return `1.${decimals}-${decimals}`;
}
